import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Sliders } from "@phosphor-icons/react";

const NUM = [
  "smart_pc_pct", "smart_min_roi_pct", "round_to_multiple",
  "high_value_threshold", "high_value_pc_pct", "no_amazon_pc_pct", "max_auto_offer", "min_offer",
  "amazon_fee_pct", "fba_fee", "shipping_cost", "prep_cost", "min_roi_threshold",
  "bl_ceiling_multiplier", "bb_undercut", "min_margin_cushion_pct",
  "deals_smart_pc_pct", "deals_smart_min_roi_pct",
  "ebay_fee_pct", "ebay_ship_est",
];

/** One place for every pricing variable. Applies to Deals AND the public site. */
export function SitewideRules() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.getSettings().then(setS).catch(() => toast.error("Failed to load rules")); }, []);
  if (!s) return null;
  const set = (k, v) => setS(x => ({ ...x, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const patch = { deals_override_enabled: !!s.deals_override_enabled, digits_rule_enabled: s.digits_rule_enabled !== false };
      for (const k of NUM) { const n = Number(s[k]); if (Number.isFinite(n)) patch[k] = n; }
      setS(await api.updateSettings(patch));
      toast.success("Sitewide rules saved · Deals and public quotes now use them");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };
  const F = ({ label, k, hint, step = "1" }) => (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">{label}</span>
      <Input type="number" step={step} value={s[k] ?? ""} onChange={(e) => set(k, e.target.value)} className="h-8 bg-[#F6F6F3] border-[#E4E4E0] font-mono text-xs" data-testid={`rules-${k}`} />
      {hint && <span className="text-[9px] font-mono text-gray-400 leading-tight">{hint}</span>}
    </label>
  );
  const Group = ({ title, children, testId }) => (
    <div className="border border-[#E4E4E0] rounded-sm" data-testid={testId}>
      <div className="px-3 py-2 border-b border-[#E4E4E0] font-head font-bold uppercase text-[10px] tracking-wider text-gray-600 bg-[#F6F6F3]">{title}</div>
      <div className="p-3 grid grid-cols-2 md:grid-cols-4 gap-3">{children}</div>
    </div>
  );
  return (
    <div className="border-2 border-[#E3000B] rounded-sm" data-testid="sitewide-rules">
      <div className="px-4 py-3 border-b border-[#E4E4E0] flex items-center justify-between bg-[#FFF1F1]">
        <span className="flex items-center gap-2 font-head font-bold uppercase text-xs tracking-wider text-[#E3000B]"><Sliders size={14} weight="bold" /> Sitewide Rules</span>
        <span className="font-mono text-[10px] text-gray-500">One source of truth for Deals + OFFERS4BRICKS quotes</span>
      </div>
      <div className="p-4 space-y-4">
        <Group title="Smart Offer" testId="rules-group-smart">
          <F label="% of PriceCharting" k="smart_pc_pct" hint="Offer = this % of the PC sealed price (BrickLink lowest if no PC)" />
          <F label="Min ROI % on Amazon" k="smart_min_roi_pct" hint="Offer is capped so Amazon (after fees) still returns this" />
          <F label="Round down to $" k="round_to_multiple" hint="Snap offers down to this multiple (0 = off; under $50 rounds to $1)" />
        </Group>
        <Group title="Tiers & limits (eBay fallback rules)" testId="rules-group-tiers">
          <F label="High-value threshold ($)" k="high_value_threshold" hint="Amazon Buy Box above this = higher return risk → eBay channel" />
          <F label="High-value % of PC" k="high_value_pc_pct" hint="% paid for sets over the threshold (0 = tier off)" />
          <F label="No-Amazon % of PC" k="no_amazon_pc_pct" hint="Set has no Amazon page → pay this % of PC (0 = manual review)" />
          <F label="Max automatic offer ($)" k="max_auto_offer" hint="Above this customers get 'we'll review personally' (0 = no ceiling)" />
          <F label="Minimum offer ($)" k="min_offer" hint="Public: below this shows 'below our minimum'" />
        </Group>
        <Group title="Amazon cost model" testId="rules-group-amazon">
          <F label="Referral fee" k="amazon_fee_pct" step="0.01" hint="0.15 = 15%" />
          <F label="FBA fee ($)" k="fba_fee" step="0.10" />
          <F label="Inbound shipping ($)" k="shipping_cost" step="0.10" />
          <F label="Prep ($)" k="prep_cost" step="0.10" />
          <F label="Target ROI % (list price)" k="min_roi_threshold" hint="Used by the legacy auto list-price generator" />
          <F label="BL ceiling ×" k="bl_ceiling_multiplier" step="0.05" />
          <F label="Undercut Buy Box ($)" k="bb_undercut" step="0.01" />
          <F label="Margin cushion %" k="min_margin_cushion_pct" />
        </Group>
        <Group title="eBay cost model (profit projections)" testId="rules-group-ebay">
          <F label="eBay final value fee %" k="ebay_fee_pct" step="0.05" hint="13.25 = 13.25% of the sale" />
          <F label="Shipping we pay ($)" k="ebay_ship_est" step="0.50" hint="Buyer pays shipping → leave at 0" />
        </Group>
        <div className="border border-[#E4E4E0] rounded-sm p-3 flex items-center gap-3" data-testid="rules-group-digits">
          <Switch checked={s.digits_rule_enabled !== false} onCheckedChange={(v) => set("digits_rule_enabled", v)} data-testid="rules-digits-toggle" className="data-[state=checked]:bg-[#E3000B]" />
          <span className="font-mono text-xs text-gray-800"><b>Only 4–5 digit set numbers get automatic offers</b> — shorter (vintage) or longer (gear/promo) numbers are accepted but shown "we need a closer look" and land in your review list. Off = price everything the rules can.</span>
        </div>
        <div className="border border-[#E4E4E0] rounded-sm p-3 space-y-3" data-testid="rules-group-deals-override">
          <div className="flex items-center gap-3">
            <Switch checked={!!s.deals_override_enabled} onCheckedChange={(v) => set("deals_override_enabled", v)} data-testid="rules-deals-override-toggle" className="data-[state=checked]:bg-[#E3000B]" />
            <span className="font-mono text-xs text-gray-800"><b>Deals manual override</b> — Deals page uses its own Smart Offer % / min ROI instead of the sitewide ones (public site is unaffected)</span>
          </div>
          {s.deals_override_enabled && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <F label="Deals: % of PriceCharting" k="deals_smart_pc_pct" />
              <F label="Deals: min ROI %" k="deals_smart_min_roi_pct" />
            </div>
          )}
        </div>
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} className="h-9 rounded-sm bg-[#E3000B] text-white font-bold uppercase tracking-wider hover:bg-[#C4000A]" data-testid="rules-save">
            {saving ? "Saving…" : "Save sitewide rules"}
          </Button>
        </div>
      </div>
    </div>
  );
}
